import React, { useState } from 'react';
import { Layout, Typography, Row, Col, Button, Card, Space, notification, Alert } from 'antd';
import { DownloadOutlined, FileTextOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { SummaryCard } from '../../components/index.js';
import { exportCommissionTracker, getExportStats } from '../../services/index.js';
import { MatchActions } from '../../types/index.js';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const ExportPage = ({ matchData = {} }) => {
  const [exporting, setExporting] = useState(false);
  
  const { matchResults = [] } = matchData;
  const exportStats = getExportStats(matchResults);

  const handleExportCommissions = async () => {
    setExporting(true);
    
    try {
      const result = await exportCommissionTracker(matchResults);
      
      notification.success({
        message: 'Export Successful',
        description: `Commission Tracker exported successfully. ${result.recordCount} records exported to ${result.filename}`,
        placement: 'topRight',
        duration: 5
      });
      
    } catch (error) {
      notification.error({
        message: 'Export Failed',
        description: error.message,
        placement: 'topRight'
      });
    } finally {
      setExporting(false);
    }
  };

  const commissionReadyCount = matchResults.filter(
    result => result.action === MatchActions.READY
  ).length;

  if (matchResults.length === 0) {
    return (
      <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        <Content style={{ padding: '24px' }}>
          <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center', paddingTop: '10%' }}>
            <FileTextOutlined style={{ fontSize: 64, color: '#d9d9d9', marginBottom: 16 }} />
            <Title level={3} type="secondary">No Data to Export</Title>
            <Paragraph type="secondary">
              Please complete the matching process before exporting commission data.
            </Paragraph>
          </div>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Content style={{ padding: '24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Export Statistics */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Total Records"
                value={exportStats.total}
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Commission Ready"
                value={exportStats.commissionReady}
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Needs Investigation"
                value={exportStats.needsInvestigation}
                valueStyle={{ color: '#faad14' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title="Not Eligible"
                value={exportStats.notEligible}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
          </Row>

          {/* Commission Tracker Export */}
          <Card 
            title={
              <Space>
                <FileTextOutlined />
                Commission Tracker Export
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {commissionReadyCount > 0 ? (
                <Alert
                  message={`${commissionReadyCount} records ready for commission export`}
                  description="These records have perfect matches and are eligible for commission calculation."
                  type="success"
                  showIcon
                  icon={<CheckCircleOutlined />}
                />
              ) : (
                <Alert
                  message="No commission-ready records found"
                  description="All records require investigation or are not commission eligible. Please review matches before exporting."
                  type="warning"
                  showIcon
                />
              )}

              <div>
                <Title level={4}>Export Details</Title>
                <Paragraph>
                  The Commission Tracker export includes the following information for each commission-ready record:
                </Paragraph>
                <ul>
                  <li>HubSpot deal link and basic deal information</li>
                  <li>WellSky database ID and client information</li>
                  <li>Sales representative and commission eligibility</li>
                  <li>Start and close dates for commission calculation</li>
                  <li>Commission amounts (calculated automatically)</li>
                  <li>Monthly statistics per sales representative</li>
                </ul>
                <Paragraph type="secondary">
                  Only records with &quot;Ready for Commission&quot; status will be included in the export.
                </Paragraph>
              </div>

              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<DownloadOutlined />}
                  loading={exporting}
                  disabled={commissionReadyCount === 0}
                  onClick={handleExportCommissions}
                >
                  {exporting ? 'Exporting...' : `Export Commission Tracker (${commissionReadyCount} records)`}
                </Button>
              </div>
            </Space>
          </Card>

          {/* Export Instructions */}
          <Card title="Export Instructions">
            <Row gutter={[24, 16]}>
              <Col xs={24} md={12}>
                <Title level={5}>Before Exporting</Title>
                <ol>
                  <li>Review all match results in the Match Dashboard</li>
                  <li>Investigate records flagged for review</li>
                  <li>Ensure commission eligibility is correctly marked</li>
                  <li>Verify sales representative assignments</li>
                </ol>
              </Col>
              <Col xs={24} md={12}>
                <Title level={5}>After Export</Title>
                <ol>
                  <li>Open the downloaded CSV file in Excel</li>
                  <li>Review commission calculations</li>
                  <li>Adjust commission rates if needed</li>
                  <li>Submit to finance for processing</li>
                </ol>
              </Col>
            </Row>
            
            <Alert
              message="File Format"
              description="The exported file will be in CSV format compatible with Excel and other spreadsheet applications. Commission amounts are calculated based on predefined rates and can be adjusted manually if needed."
              type="info"
              style={{ marginTop: 16 }}
            />
          </Card>
        </div>
      </Content>
    </Layout>
  );
};

export default ExportPage;
